// useDebouncedDispatch.ts
import { useCallback, useEffect, useRef } from "react";
import { useDispatch } from "react-redux";
// ak máš AppDispatch, importni si ho a využi namiesto `any`
// import type { AppDispatch } from "@/store";

type AnyThunk = (...args: any[]) => any; // RTK thunk alebo funkcia vracajúca Promise
type Dispatchable = any; // AnyAction | ThunkAction | atď.

type Bucket = {
    timer: ReturnType<typeof setTimeout> | null;
    lastAction: Dispatchable | null;
    resolvers: Array<(v: any) => void>;
    rejecters: Array<(e: any) => void>;
};

type Options = {
    delay?: number;                               // default 500 ms
    getKey?: (action: Dispatchable) => string;    // ako získať kľúč zo vstupu (ak nepošleš key)
};

type CallOpts = {
    key?: string;  // manuálny kľúč – odporúčané: hash payloadu/parametrov
};

export function useDebouncedRequest(opts: Options = {}) {
    const { delay = 1000, getKey } = opts;
    // ak máš AppDispatch, použij: const dispatch = useDispatch<AppDispatch>();
    const dispatch = useDispatch<any>();

    const bucketsRef = useRef<Map<string, Bucket>>(new Map());
    const unmountedRef = useRef(false);

    const defaultGetKey = useCallback((action: Dispatchable) => {
        // bezpečný fallback – ak je to objekt, skúsime JSON; ak funkcia, použijeme jej meno
        if (action && typeof action === "object") {
            try { return JSON.stringify(action); } catch { /* noop */ }
        }
        if (typeof action === "function") {
            return action.name || "fn";
        }
        return String((action as any)?.type ?? "unknown");
    }, []);

    const resolveAll = (bucket: Bucket, value: any) => {
        bucket.resolvers.forEach(r => { try { r(value); } catch {} });
        bucket.resolvers = [];
        bucket.rejecters = [];
    };

    const rejectAll = (bucket: Bucket, error: any) => {
        bucket.rejecters.forEach(rj => { try { rj(error); } catch {} });
        bucket.resolvers = [];
        bucket.rejecters = [];
    };

    const runDispatch = useCallback(async (act: Dispatchable) => {
        // dispatch môže v RTK vracať promise s .unwrap()
        const res = dispatch(act as any);
        if (res && typeof (res as any).unwrap === "function") {
            // RTK createAsyncThunk – vráť priamo payload alebo vyhoď error
            return (res as any).unwrap();
        }
        return await res; // iné thunky/promise
    }, [dispatch]);

    const debouncedRequest = useCallback(
        <R = any>(action: Dispatchable, callOpts: CallOpts = {}): Promise<R> => {
            const key = callOpts.key ?? (getKey ?? defaultGetKey)(action);
            const map = bucketsRef.current;
            let bucket = map.get(key);

            if (!bucket) {
                bucket = { timer: null, lastAction: null, resolvers: [], rejecters: [] };
                map.set(key, bucket);
            }

            bucket.lastAction = action;

            // sľub pre tento konkrétny call
            const promise = new Promise<R>((resolve, reject) => {
                bucket!.resolvers.push(resolve);
                bucket!.rejecters.push(reject);
            });

            // reset timer
            if (bucket.timer) clearTimeout(bucket.timer);
            bucket.timer = setTimeout(async () => {
                // po „tichu“ spustíme poslednú požiadavku v tomto kľúči
                const current = map.get(key);
                if (!current) return;

                const act = current.lastAction;
                // z mapy vyhodíme bucket – nový burst začne novým bucketom
                map.delete(key);

                if (unmountedRef.current) {
                    rejectAll(current, new Error("Component unmounted"));
                    return;
                }

                try {
                    const value = await runDispatch(act);
                    resolveAll(current, value);
                } catch (e) {
                    rejectAll(current, e);
                }
            }, delay);

            return promise;
        },
        [delay, getKey, defaultGetKey, runDispatch]
    );

    const cancel = useCallback((key?: string) => {
        const map = bucketsRef.current;
        if (key) {
            const b = map.get(key);
            if (!b) return;
            if (b.timer) clearTimeout(b.timer);
            map.delete(key);
            rejectAll(b, new Error("Debounced dispatch canceled"));
            return;
        }
        // cancel všetko
        for (const [, b] of map) {
            if (b.timer) clearTimeout(b.timer);
            rejectAll(b, new Error("Debounced dispatch canceled"));
        }
        map.clear();
    }, []);

    const flush = useCallback(async (key: string) => {
        // okamžite spusti čakajúci bucket daného kľúča
        const map = bucketsRef.current;
        const b = map.get(key);
        if (!b) return;
        if (b.timer) clearTimeout(b.timer);
        map.delete(key);
        try {
            const value = await runDispatch(b.lastAction);
            resolveAll(b, value);
            return value;
        } catch (e) {
            rejectAll(b, e);
            throw e;
        }
    }, [runDispatch]);

    useEffect(() => {
        return () => {
            unmountedRef.current = true;
            // pri unmount rejectni všetko čakajúce
            for (const [, b] of bucketsRef.current) {
                if (b.timer) clearTimeout(b.timer);
                rejectAll(b, new Error("Component unmounted"));
            }
            bucketsRef.current.clear();
        };
    }, []);

    return { debouncedRequest, cancel, flush };
}
